import { useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../api/client'
import { useQuery } from '../api/hooks'
import type { JournalEntryDetail, JournalEntryLine } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import { Attachments } from '../components/Attachments'
import {
  ErrorBanner,
  Loading,
  Money,
  Panel,
  StatusTag,
  Tag,
  formatDate,
  formatDateTime,
  money,
} from '../components/ui'
import { ReverseEntry } from './JournalPage'

/**
 * One entry, line by line — where every report drills through to.
 *
 * <p>The reports answer "what is the balance"; this answers "which entry made it that". Until this
 * page existed <code>/journal/:id</code> rendered the list, so following a figure back to its entry
 * meant reading the entry number off the report and searching for it by hand.</p>
 *
 * <p>Read-only on purpose. A posted entry is never edited — the only thing you can do to it from
 * here is reverse it, which writes a second entry and leaves this one standing (ARCHITECTURE §3).</p>
 */
export function JournalEntryPage() {
  const { id } = useParams<{ id: string }>()
  const { can } = useAuth()
  const [reversing, setReversing] = useState(false)
  const [reviewing, setReviewing] = useState(false)
  const [reviewError, setReviewError] = useState<unknown>()

  const entry = useQuery<JournalEntryDetail>(id ? `/journal/${id}` : null, [id])

  /** Stamps or withdraws a sign-off, then reloads so the page re-renders from the server. */
  async function review(approve: boolean) {
    setReviewing(true)
    setReviewError(undefined)
    try {
      await api(`/journal/${id}/review`, { method: approve ? 'POST' : 'DELETE' })
      entry.reload()
    } catch (caught) {
      setReviewError(caught)
    } finally {
      setReviewing(false)
    }
  }

  if (entry.loading) {
    return <Loading what="Loading the entry" />
  }

  if (entry.error || !entry.data) {
    return (
      <>
        <ErrorBanner error={entry.error} />
        <p>
          <Link to="/journal">← Back to the journal</Link>
        </p>
      </>
    )
  }

  const found = entry.data

  // Debits and credits are equal on every posted entry — that is rule 1 — so the total is one
  // number, not two. Showing both anyway is what makes it obvious when you are looking at a draft
  // that does not yet balance.
  const totalDebit = found.lines.reduce((sum, l) => sum + l.baseDebit, 0)
  const totalCredit = found.lines.reduce((sum, l) => sum + l.baseCredit, 0)
  const balanced = Math.abs(totalDebit - totalCredit) < 0.005

  // Only shown when something on the entry is not in the base currency. In an all-EGP entry the
  // transaction and base columns are the same number twice, which is noise.
  const hasForeignCurrency = found.lines.some((l) => l.rate !== 1)

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <Link to="/journal">← Journal</Link>
        <div className="spacer" />
        {found.status === 'Posted' && can(P.journal.reverse) && (
          <button className="subtle" onClick={() => setReversing(true)}>
            Reverse this entry
          </button>
        )}

        {/* A reviewer usually arrives here from the queue to read the lines before signing off, so
            the action belongs on the page they read it on rather than only on the list. */}
        {found.status === 'Draft' && can(P.journal.review) && (
          <button className="subtle" disabled={reviewing} onClick={() => void review(!found.reviewedAt)}>
            {found.reviewedAt ? 'Withdraw review' : 'Review this entry'}
          </button>
        )}
      </div>

      <ErrorBanner error={reviewError} onDismiss={() => setReviewError(undefined)} />

      <Panel
        title={
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="code">{found.entryNo ?? 'Unposted draft'}</span>
            <StatusTag status={found.status} />
            {/* The dual-basis flag decides which of the two P&L columns this entry reaches, so it
                belongs in the heading rather than in a details list (BUILD_PLAN §3.1). */}
            <Tag kind={found.basis === 'Both' ? 'default' : 'warn'}>{found.basis}</Tag>
          </h2>
        }
      >
        <p style={{ fontSize: 16, marginTop: 0 }}>{found.description}</p>
        {found.descriptionAr && <p className="soft" dir="rtl">{found.descriptionAr}</p>}

        <dl className="grid cols-3" style={{ margin: 0 }}>
          <Detail label="Posting date">{formatDate(found.postingDate)}</Detail>
          <Detail label="Document date">{formatDate(found.documentDate) || '—'}</Detail>
          <Detail label="Book">{found.book}</Detail>
          <Detail label="Reference">{found.reference || '—'}</Detail>
          <Detail label="Posted by">{found.postedBy || '—'}</Detail>
          <Detail label="Posted at">{formatDateTime(found.postedAt) || '—'}</Detail>

          {/* Only where a review actually happened. On an office that does not require one these
              would be two permanent dashes, which is a column that says nothing on every row. */}
          {(found.reviewedAt || found.createdBy) && (
            <>
              <Detail label="Entered by">{found.createdBy || '—'}</Detail>
              <Detail label="Reviewed by">{found.reviewedBy || '—'}</Detail>
              <Detail label="Reviewed at">{formatDateTime(found.reviewedAt) || '—'}</Detail>
            </>
          )}
        </dl>

        {/* Where the entry came from. An entry with a source was generated by a screen that had its
            own rules, and knowing which one is the difference between "somebody typed this" and
            "this is the safe book". */}
        {found.sourceType && (
          <p className="muted" style={{ fontSize: 12 }}>
            Generated by <strong>{sourceLabel(found.sourceType)}</strong> rather than typed into the
            journal.
          </p>
        )}

        {/* An accrual and its unwind. Deliberately not the reversal banners below: this entry is
            still posted and still correct for its own period, so saying "reversed" here would
            report a month-end estimate that worked exactly as intended as though it were a
            mistake (POSTING_MATRIX §C5). */}
        {found.autoReverseNextPeriod && !found.autoReversedById && (
          <div className="banner info">
            Flagged to reverse at the start of the next period. The unwinding entry is raised when
            this period is closed — the close is blocked until it has been.
          </div>
        )}

        {found.autoReversedById && (
          <div className="banner info">
            <div>
              <strong>Unwound into the next period as planned.</strong> This accrual is still
              posted and still stands for its own period; the entry that reverses it is{' '}
              <Link to={`/journal/${found.autoReversedById}`} className="code">
                {found.autoReversedByNo ?? 'the auto-reversal'}
              </Link>
              .
            </div>
          </div>
        )}
      </Panel>

      {/* The two ends of a reversal. Both entries stay in the ledger permanently and each points at
          the other, so whichever one you arrived at, the other is one click away. */}
      {found.reversalOfId && (
        <div className="banner warn">
          <div>
            <strong>This entry reverses another.</strong> It mirrors{' '}
            <Link to={`/journal/${found.reversalOfId}`} className="code">
              {found.reversalOfNo ?? 'the original'}
            </Link>
            {found.reversalReason && <> — {found.reversalReason}</>}
          </div>
        </div>
      )}

      {found.reversedById && (
        <div className="banner warn">
          <div>
            <strong>This entry has been reversed</strong> by{' '}
            <Link to={`/journal/${found.reversedById}`} className="code">
              {found.reversedByNo ?? 'a later entry'}
            </Link>
            {found.reversalReason && <> — {found.reversalReason}</>}. Both remain in the ledger; the
            pair nets to nothing.
          </div>
        </div>
      )}

      <Panel flush title="Lines">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }} className="num">
                  #
                </th>
                <th>Account</th>
                <th style={{ width: 140 }}>Party</th>
                <th style={{ width: 110 }}>Nature</th>
                {hasForeignCurrency && (
                  <>
                    <th style={{ width: 70 }}>Cur.</th>
                    <th style={{ width: 90 }} className="num">
                      Rate
                    </th>
                    <th style={{ width: 120 }} className="num">
                      Txn Dr
                    </th>
                    <th style={{ width: 120 }} className="num">
                      Txn Cr
                    </th>
                  </>
                )}
                <th style={{ width: 130 }} className="num">
                  Debit
                </th>
                <th style={{ width: 130 }} className="num">
                  Credit
                </th>
                <th>Memo</th>
              </tr>
            </thead>
            <tbody>
              {found.lines.map((line) => (
                <tr key={line.id}>
                  <td className="num muted">{line.lineNo}</td>
                  <td>
                    <span className="code">{line.code}</span> {line.nameEn}
                    <LineNotes line={line} />
                  </td>
                  <td className="soft">{line.partyName ?? '—'}</td>
                  <td>
                    {line.clientFundNature ? (
                      <Tag kind="info">{line.clientFundNature}</Tag>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  {hasForeignCurrency && (
                    <>
                      <td className="code">{line.currency}</td>
                      <td className="num muted">{line.rate === 1 ? '—' : line.rate}</td>
                      <td className="num soft">
                        <Money value={line.txnDebit} blankZero />
                      </td>
                      <td className="num soft">
                        <Money value={line.txnCredit} blankZero />
                      </td>
                    </>
                  )}
                  <td className="num">
                    <Money value={line.baseDebit} blankZero />
                  </td>
                  <td className="num">
                    <Money value={line.baseCredit} blankZero />
                  </td>
                  <td className="soft">{line.memo ?? ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={hasForeignCurrency ? 8 : 4}>
                  {balanced ? (
                    'Totals'
                  ) : (
                    <span className="negative">
                      Out by {money(Math.abs(totalDebit - totalCredit))} — this is a draft
                    </span>
                  )}
                </td>
                <td className="num strong">
                  <Money value={totalDebit} />
                </td>
                <td className="num strong">
                  <Money value={totalCredit} />
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </Panel>

      {/* The paper behind the figures. On an entry that came from the intake register the scan is
          usually on the document rather than here — this is for what supports the entry itself: a
          bank advice behind a transfer, a signed schedule behind a salary accrual. */}
      <Panel title="Attachments">
        <Attachments
          entityType="JournalEntry"
          entityId={found.id}
          hint="The paper behind this entry — a bank advice, a signed schedule. Up to 20 MB each."
        />
      </Panel>

      {reversing && (
        <ReverseEntry
          entry={found}
          onClose={() => setReversing(false)}
          onSaved={() => {
            setReversing(false)
            entry.reload()
          }}
        />
      )}
    </>
  )
}

/**
 * What a line did to the sub-ledger and what it was coded to, printed under the account.
 *
 * The first two are opposite halves of the same question. `settlesDocNo` says which document this
 * line cleared; the instalment pair says how the document it *raised* is going to be cleared.
 * Neither is visible anywhere else on a posted entry, and both are the reason somebody drills
 * through to it.
 *
 * The cost centre joins them here rather than as a column of its own, for the same reason the
 * transaction-currency columns are hidden on an all-EGP entry: it is set on the income and expense
 * lines only, so a column would be empty on most rows of most entries.
 */
function LineNotes({ line }: { line: JournalEntryLine }) {
  const notes = [
    line.settlesDocNo ? `settles ${line.settlesDocNo}` : null,
    line.loanInstalmentCount != null
      ? `repaid over ${line.loanInstalmentCount} × ${money(line.loanInstalmentAmount ?? 0)}`
      : null,
    line.departmentName ? `${line.departmentCode} · ${line.departmentName}` : null,
  ].filter((note): note is string => note !== null)

  if (notes.length === 0) {
    return null
  }

  return (
    <div className="muted" style={{ fontSize: 12 }}>
      {notes.join(' · ')}
    </div>
  )
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </div>
  )
}

/** `SourceType` is the generating entity's class name, which is not a phrase for a screen. */
function sourceLabel(sourceType: string): string {
  switch (sourceType) {
    case 'CashBookEntry':
      return 'a cash or safe book row'
    case 'DocumentIntake':
      return 'the document intake register'
    case 'Reversal':
      return 'a reversal'
    case 'AutoReversal':
      return 'the period close, unwinding an accrual'
    default:
      return sourceType
  }
}
